import React from 'react';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import styles from '../css/vdr.module.css';

/**
 * Upload new version — replaces the current file and keeps the previous file in version history.
 */
const UploadNewVersionModal = ({ open, onClose, file, fileName, currentVersionLabel, onUpload }) => {
    const [picked, setPicked] = React.useState(null);
    const [versionNote, setVersionNote] = React.useState('');
    const [dragging, setDragging] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const inputRef = React.useRef(null);
    const displayName = fileName || file?.document_name || file?.name || 'this file';
    const versionLabel = currentVersionLabel || 'v1';

    React.useEffect(() => {
        if (open) {
            setPicked(null);
            setVersionNote('');
            setDragging(false);
        }
    }, [open]);

    if (!open) return null;

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Upload new version</h2>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <p className={styles.modalDesc}>
                        Replacing: {displayName} · {versionLabel}
                    </p>

                    <div
                        className={`${styles.dropZone} ${styles.dropZoneDashed} ${dragging ? styles.dropZoneActive : ''}`}
                        onDragOver={e => {
                            e.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={e => {
                            e.preventDefault();
                            setDragging(false);
                            setPicked(e.dataTransfer.files?.[0] || null);
                        }}
                        onClick={() => inputRef.current?.click()}
                    >
                        <p className={styles.dropZoneTitle}>{picked ? picked.name : 'Drag file here or choose file'}</p>
                        <input
                            ref={inputRef}
                            type="file"
                            style={{ display: 'none' }}
                            onChange={e => setPicked(e.target.files?.[0] || null)}
                        />
                    </div>

                    <label className={styles.fieldLabel}>Version note</label>
                    <input
                        className={styles.input}
                        value={versionNote}
                        onChange={e => setVersionNote(e.target.value)}
                        placeholder="Optional"
                    />
                </div>
                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={async () => {
                            if (!picked || !onUpload) return;
                            setLoading(true);
                            try {
                                await onUpload({ file: picked, note: versionNote });
                                onClose();
                            } catch (error) {
                                const detail = error?.response?.data?.detail;
                                toast.error(detail || 'Could not upload this version');
                            } finally {
                                setLoading(false);
                            }
                        }}
                        disabled={!picked || loading}
                    >
                        {loading ? 'Uploading…' : 'Upload version'}
                    </button>
                    <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadNewVersionModal;
